import type { ReactNode } from "react";
import { Button as RACButton, type ButtonProps as RACButtonProps } from "react-aria-components";
import { Icon, type TablerIcon } from "../icon";
import { Tooltip } from "../tooltip";
import { cx, tv, type VariantProps } from "../tv";

export const button = tv({
  base: [
    "relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center gap-1.5 whitespace-nowrap font-medium outline-none",
    "transition-[translate,scale,box-shadow,background-color,color] duration-(--dur-fast) ease-out",
    "focus-visible:shadow-focus pressed:scale-[.98] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  ],
  variants: {
    variant: {
      // the one filled petrol action per view (DESIGN-RULES 2)
      primary: [
        "text-accent-fg",
        "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--accent)_88%,white),var(--accent))]",
        "shadow-[inset_0_1px_0_oklch(100%_0_0/.22),0_0_0_1px_color-mix(in_oklch,var(--accent)_70%,black),0_4px_10px_-4px_color-mix(in_oklch,var(--accent)_60%,transparent)]",
        "hovered:-translate-y-px",
        "dark:bg-[linear-gradient(180deg,oklch(76%_0.085_200),oklch(68%_0.09_202))] dark:shadow-[inset_0_1px_0_oklch(100%_0_0/.3),0_6px_14px_-6px_oklch(60%_0.1_200/.5)]",
      ],
      secondary: "bg-panel-solid text-fg shadow-card hovered:-translate-y-px",
      ghost: "bg-transparent text-fg-muted hovered:bg-hover hovered:text-fg",
      danger: "bg-danger-solid text-danger-solid-fg shadow-card hovered:-translate-y-px",
    },
    size: {
      md: "h-8 rounded-md px-3 text-sm",
      sm: "h-7 rounded-sm px-2.5 text-xs",
    },
    iconOnly: { true: "px-0" },
  },
  compoundVariants: [
    { iconOnly: true, size: "md", class: "w-8" },
    { iconOnly: true, size: "sm", class: "w-7" },
  ],
  defaultVariants: { variant: "secondary", size: "md" },
});

type Common = {
  icon?: TablerIcon;
  iconEnd?: TablerIcon;
  /** Shown in a tooltip when the control is disabled. Every disabled control must say why (DESIGN-RULES 9). */
  disabledReason?: string;
  /** Keyboard shortcut shown in the tooltip. */
  shortcut?: string;
  className?: string;
};

export type ButtonProps = Omit<RACButtonProps, "className" | "children"> & VariantProps<typeof button> & Common & { children?: ReactNode };

function withReason(el: React.ReactElement, isDisabled: boolean | undefined, reason?: string) {
  if (!isDisabled || !reason) return el;
  // A disabled button gets no pointer events, so the reason lives on a focusable wrapper.
  return (
    <Tooltip content={reason} wrap>
      <span tabIndex={0} className="inline-flex rounded-md outline-none focus-visible:shadow-focus">{el}</span>
    </Tooltip>
  );
}

export function Button({ variant, size, icon, iconEnd, disabledReason, shortcut, className, children, isPending, ...rest }: ButtonProps) {
  const el = (
    <RACButton {...rest} isPending={isPending} className={cx(button({ variant, size }), className)}>
      {icon && <Icon icon={icon} size={size === "sm" ? "chip" : "row"} className={cx(variant !== "primary" && variant !== "danger" && "text-fg-muted", isPending && "opacity-0")} />}
      <span className={cx(isPending && "opacity-0")}>{children}</span>
      {iconEnd && <Icon icon={iconEnd} size={size === "sm" ? "chip" : "row"} className="text-fg-subtle" />}
      {isPending && <Pending />}
    </RACButton>
  );
  if (rest.isDisabled) return withReason(el, rest.isDisabled, disabledReason);
  return shortcut ? <Tooltip content={children} shortcut={shortcut}>{el}</Tooltip> : el;
}

export type IconButtonProps = Omit<RACButtonProps, "className" | "children"> & VariantProps<typeof button> & Common & {
  icon: TablerIcon;
  /** Required: icon-only buttons always have a name and a tooltip (DESIGN-RULES 7). */
  label: string;
  /** true shows a dot; a number shows that count (0 shows nothing), capped at 99+ (X21-OPS-24). */
  badge?: boolean | number;
};

export function IconButton({ icon, label, variant = "ghost", size, shortcut, disabledReason, className, badge, ...rest }: IconButtonProps) {
  const el = (
    <RACButton {...rest} aria-label={label} className={cx(button({ variant, size, iconOnly: true }), className)}>
      <Icon icon={icon} size="row" />
      {typeof badge === "number"
        ? badge > 0 && (
            <span aria-hidden className="tabular absolute end-0.5 top-0.5 min-w-[18px] rounded-pill bg-[var(--code-ember-solid)] px-1 text-center text-[10px] leading-[18px] font-semibold text-[var(--panel-solid)] shadow-[0_0_0_2px_var(--panel-solid)]">
              {badge > 99 ? "99+" : badge}
            </span>
          )
        : badge && <span aria-hidden className="absolute end-1.5 top-1.5 size-[7px] rounded-pill bg-[var(--code-ember-solid)] shadow-[0_0_0_2px_var(--panel-solid)]" />}
    </RACButton>
  );
  if (rest.isDisabled) return withReason(el, rest.isDisabled, disabledReason ?? label);
  return <Tooltip content={label} shortcut={shortcut}>{el}</Tooltip>;
}

/** Three soft dots, shown in place of the label while an action is pending (the button keeps its width). */
function Pending() {
  return (
    <span aria-hidden className="absolute inset-0 grid place-items-center">
      <span className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <i key={i} className="size-1 rounded-pill bg-current animate-[os-dot_900ms_var(--ease-in-out)_infinite]" style={{ animationDelay: `${i * 120}ms` }} />
        ))}
      </span>
    </span>
  );
}
